/**
 * Clap detection, pure. Feed it the microphone's loudness once per frame (`sample(level, t)`),
 * it tells you when two claps have landed within a second. A clap is a sudden spike: the level
 * jumps to several times the recent background and falls back within a few frames.
 */
export interface ClapRule {
  /** the spike must be this many times the background */
  ratio: number;
  /** and at least this loud, so a quiet room's noise floor can't trigger it */
  floor: number;
  /** two claps within this many seconds count */
  window: number;
  /** ignore spikes this close together (one clap rings for a few frames) */
  refractory: number;
}

export const CLAP_RULE: ClapRule = { ratio: 3.5, floor: 0.08, window: 1.0, refractory: 0.15 };

export class ClapDetector {
  private background = 0.02;
  private lastSpikeAt = -Infinity;
  private spikes: number[] = [];
  /** game sounds would clap at themselves: hold off until this time */
  private mutedUntil = -Infinity;

  private rule: ClapRule;

  constructor(rule: ClapRule = CLAP_RULE) {
    this.rule = rule;
  }

  /** the game is about to make a loud noise of its own */
  mute(untilT: number): void {
    this.mutedUntil = Math.max(this.mutedUntil, untilT);
  }

  /** feed one loudness reading (0..1-ish RMS) at time t; true when a double clap has just completed */
  sample(level: number, t: number): boolean {
    const r = this.rule;
    const isSpike = level >= r.floor && level >= this.background * r.ratio && t >= this.mutedUntil && t - this.lastSpikeAt >= r.refractory;
    // the background follows the level slowly, and does not learn from spikes
    if (!isSpike) this.background += (level - this.background) * 0.08;
    if (!isSpike) return false;
    this.lastSpikeAt = t;
    this.spikes = this.spikes.filter((s) => t - s <= r.window);
    this.spikes.push(t);
    if (this.spikes.length >= 2) {
      this.spikes = [];
      return true;
    }
    return false;
  }
}
